import React, { useState } from 'react';
import LayoutWithSidebar from 'components/v2/LayoutWithSidebar';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Checkbox,
  Glyphicon,
  OverlayTrigger,
  Popover,
} from 'react-bootstrap';
import { titleize, unescapeHTML } from 'underscore.string';
import { Link } from 'react-router-dom';
import CustomersAPIUtils from 'utils/CustomersAPIUtils';
import SessionStore from 'stores/SessionStore';
import ToastStore from 'stores/ToastStore';
import UserWebAPIUtils from 'utils/UserWebAPIUtils';
import CargomaticCard from 'components/v2/card';
import Badge from 'components/v2/Badge';
import EditButton from 'components/v2/buttons/EditButton';
import LabelValue from 'components/ui/tailwind/label-value';
import { unescapeSpecialChars } from 'helpers/helper_functions';
import {
  getAddressString,
  hasAddress,
} from 'utils/transformers/AddressTransformer';
import CargomaticModal from 'components/v2/modal';
import { AuthorizationWrapper } from 'components/wrappers/AuthorizationWrapper';
import { REGIONS } from 'constants/Regions';
import { createPhoneNumberWithExtension } from 'utils/formatters/Phone';
import Sidebar from '../../carrier/AdminSidebar';
import { Avatar } from '../Avatar';
import Capability from '../Capability';
import CarrierForm from './carrier-form';
import AccountContacts from '../../../v2/contacts';
import CarrierAccountAccess from './CarrierAccountAccess';

const STATUSES = SessionStore.hasComplianceRole()
  ? {
      AWAITING_TRAINING: 'Awaiting Training',
      TRAINED: 'Trained',
      BROKER: 'Broker',
    }
  : {
      AWAITING_TRAINING: 'Awaiting Training',
      TRAINED: 'Trained',
    };
const STATUS_OPTIONS = SessionStore.hasComplianceRole()
  ? ['Trained', 'MVP', 'Broker']
  : ['Trained', 'MVP'];

const loginAs = async (grantorId) => {
  const admin = SessionStore.getUser();
  try {
    const user = await UserWebAPIUtils.loginAs(admin._id, grantorId).catch(
      (err) => {
        ToastStore.getNotifications().push({
          title: 'Error',
          body: err,
          level: 'error',
        });
      },
    );

    if (user) {
      window.location.href = '/';
    }
  } catch (err) {
    ToastStore.getNotifications().push({
      title: 'Error',
      body: err,
      level: 'error',
    });
  }
};

const renderAddress = (address) => {
  const addressString = getAddressString(address);
  return <LabelValue value={addressString} icon="map-marker" />;
};

const handleStatusUpdates = (type, value, _id) => {
  let update = { _id };
  switch (type) {
    case 'Trained':
      update.status = value ? STATUSES.TRAINED : STATUSES.AWAITING_TRAINING;
      break;
    case 'MVP':
      update.mvp = value;
      break;
    case 'Broker':
      update.is_broker = value;
      break;
    default:
  }
  CustomersAPIUtils.updateCarrier(update);
};

const CarrierAccountDetails = (props) => {
  const { t } = useTranslation();
  const {
    user: {
      name,
      _id,
      first_name,
      last_name,
      phone,
      phone_extension,
      email,
      address,
      is_broker,
      authorized: authorization,
      has_flatbed_trucks,
      hazmat_certified,
      tsa_certified,
      is_owner_operator,
      is_dray,
      test,
      enterprise,
      preferred_carrier,
      status,
      mvp,
      bank_verified,
      subsidiary,
      driver_categorization,
      lease_on,
    },
    verification: { isVerified, authorized, bankTransfer, documentAlert },
    onOpenJobs,
    onOpenPassword,
    onOpenAccountRequest,
    handleStatusUpdates,
    hasLoginAsPermission,
  } = props;

  const checkStatus = (type) => {
    switch (type) {
      case 'Trained':
        return status === STATUSES.TRAINED;
      case 'MVP':
        return mvp;
      case 'Broker':
        return is_broker;
      default:
    }
  };

  const renderStatus = () => {
    return (
      <div className="dropdown-filter-wrapper mb-4 flex justify-center">
        <OverlayTrigger
          className="dropdown-filter-wrapper"
          rootClose
          trigger="click"
          placement="bottom"
          overlay={renderStatusPopup()}
        >
          <div>
            <Button style={{ outline: 'none' }} bsSize="small">
              <strong>{t('STATUS')}</strong>
              <Glyphicon glyph="chevron-down" className="button-down-glyph" />
            </Button>
          </div>
        </OverlayTrigger>
      </div>
    );
  };

  const renderStatusPopup = () => {
    const isSupport = SessionStore.isSupport();
    return (
      <Popover id="carrier-status-popup">
        <ul className="filter-dropdown-wrapper">
          {STATUS_OPTIONS.map((type) => {
            return (
              <li key={type}>
                <Checkbox
                  onChange={(e) =>
                    handleStatusUpdates(type, e.target.checked, _id)
                  }
                  checked={checkStatus(type)}
                  disabled={type === 'Trained' && !isSupport}
                >
                  {t(type.toUpperCase())}
                </Checkbox>
              </li>
            );
          })}
        </ul>
      </Popover>
    );
  };

  return (
    <div className="">
      <CargomaticCard className="mb-6">
        <div className="flex justify-center mb-2">
          <Avatar
            key={_id}
            userId={_id}
            className="h-32 w-32 cursor-pointer rounded-full"
          />
        </div>
        <div className="relative w-min my-0 mx-auto">
          <h3 className="my-2 text-xl text-center text-grey-darker whitespace-nowrap">{`${unescapeHTML(
            name,
          )}`}</h3>
          {mvp ? (
            <img
              src="/img/cargomatic-circle.png"
              className="absolute -top-1 -right-4 w-4"
              alt="MVP"
            />
          ) : null}
        </div>
        <div className="flex flex-wrap justify-center -mx-2">
          <div className="flex-none px-2 mb-2">
            <Badge color="blue" size="large" rounded>
              {titleize(t('CARRIER', { count: 1 }))}
            </Badge>
          </div>
          <div className="flex-none px-2 mb-2">
            <Badge
              color={authorized === 'authorized' ? 'brand' : 'red'}
              size="large"
              rounded
            >
              {t(authorized.toUpperCase())}
            </Badge>
          </div>
          {is_broker && (
            <div className="flex-none px-2 mb-2">
              <Badge color="orange" size="large" rounded>
                {t('BROKER')}
              </Badge>
            </div>
          )}
          <div className="flex-none px-2 mb-2">
            <Badge
              color={isVerified === 'verified' ? 'brand' : 'red'}
              size="large"
              rounded
            >
              {t('COMPANY_REPRESENTATIVE', {
                isVerified: t(isVerified.toUpperCase()),
              })}
            </Badge>
          </div>
          <div className="flex-none px-2 mb-2">
            <Badge
              color={bankTransfer === 'enabled' ? 'brand' : 'red'}
              size="large"
              rounded
            >
              {t('BANK_TRANSFERS', {
                bankTransfer: t(bankTransfer.toUpperCase()),
              })}
            </Badge>
          </div>
          {isVerified === ' unverified' && (
            <div className="flex-none px-2 mb-2">
              <Badge color="red" size="large" rounded>
                {documentAlert}
              </Badge>
            </div>
          )}
        </div>
        <div className="flex justify-center mt-4">
          <button className="button text-grey-darker px-2" onClick={onOpenJobs}>
            {t('VIEW_JOBS')}
          </button>
          <Link
            className="button text-grey-darker no-underline px-2"
            to={`/carrier-metrics/${_id}`}
          >
            {t('VIEW_SCORECARD')}
          </Link>
          {SessionStore.isSupport() && (
            <button
              className="button text-grey-darker px-2"
              onClick={() => loginAs(_id)}
            >
              {t('LOG_IN_AS')}
            </button>
          )}
        </div>
        {renderStatus()}
      </CargomaticCard>
      <CargomaticCard
        className="mb-6"
        header={() => (
          <header
            onClick={() => props.onOpenModal('account')}
            className="flex flex-wrap items-center justify-between"
          >
            <h3 className="my-0">{t('ACCOUNT')}</h3>

            <EditButton />
          </header>
        )}
      >
        <LabelValue value={`${unescapeSpecialChars(name)}`} icon="home" />
        <LabelValue
          value={`${unescapeSpecialChars(first_name)} ${unescapeSpecialChars(
            last_name,
          )}`}
          icon="user"
        />
        <LabelValue
          value={createPhoneNumberWithExtension(
            phone,
            phone_extension,
            t('EXT'),
          )}
          icon="phone"
        />
        <LabelValue value={`${email}`} icon="envelope" />
        <hr />
        <div className="">
          <header
            onClick={() => props.onOpenModal('address')}
            className="flex flex-wrap justify-between"
          >
            <h3 className="my-0">{t('ADDRESS')}</h3>
            <EditButton />
          </header>
          <div className="mb-6">
            {hasAddress(address) && renderAddress(address)}
            {!hasAddress(address) && (
              <LabelValue value={t('NO_ADDRESS_ON_FILE')} icon="map-marker" />
            )}
          </div>
        </div>
      </CargomaticCard>
      <CargomaticCard
        className="mb-6"
        header={() => (
          <header
            onClick={() => props.onOpenModal('capabilities')}
            className="flex flex-wrap justify-between"
          >
            <h3 className="my-0">{t('CAPABILITIES')}</h3>
            <EditButton />
          </header>
        )}
      >
        <AuthorizationWrapper excludedRegions={[REGIONS.US]}>
          <Capability label={t('BANK_VERIFIED')} checked={bank_verified} />
        </AuthorizationWrapper>
        <Capability label={t('AUTHORIZED')} checked={authorization} />
        <Capability label={t('ENTERPRISE_CARRIER')} checked={enterprise} />
        <Capability
          label={t('PREFERRED_CARRIER')}
          checked={preferred_carrier}
        />
        <Capability
          label={`${t('HAS_FLATBED_TRUCKS')} ?`}
          checked={has_flatbed_trucks}
        />
        <Capability label={t('HAZMAT_CERTIFIED')} checked={hazmat_certified} />
        <Capability label={t('TSA_CERTIFIED')} checked={tsa_certified} />
        <Capability label={t('OWNER_OPERATOR')} checked={is_owner_operator} />
        <Capability label={t('DRAY_OPERATOR')} checked={is_dray} />
        <Capability label={t('TEST')} checked={test} />
        <Capability label={t('LEASE_ON')} checked={lease_on} />
        <hr />
        <div className="w-full flex justify-between py-2">
          <label className="form-label">Subsidiary</label>
          <p>{subsidiary ? t(subsidiary.toUpperCase()) : 'Not Set'}</p>
        </div>
        <div className="w-full flex justify-between py-2">
          <label className="form-label">{t('DRIVER_CATEGORIZATION')}</label>
          <p>
            {driver_categorization
              ? t(driver_categorization.toUpperCase())
              : 'Not Set'}
          </p>
        </div>
      </CargomaticCard>
      <div className="my-6">
        <button
          onClick={() => onOpenPassword()}
          className="w-full button button-hollow-red"
        >
          {t('CHANGE_PASSWORD')}
        </button>
      </div>
      {SessionStore.isSupport() && !hasLoginAsPermission && (
        <div className="my-6">
          <button
            onClick={() => onOpenAccountRequest()}
            className="w-full button button-green"
            disabled={!SessionStore.isSupport()}
          >
            {t('REQUEST_PERMISSION_TO_LOGIN_AS')}
          </button>
        </div>
      )}
    </div>
  );
};

const hasLegalEntityDocumentDue = (fieldsNeeded) => {
  return fieldsNeeded.indexOf('legal_entity.verification.document') >= -1;
};

const hasDocumentDue = (requirements) => {
  const verificationFields = ['currently_due', 'eventually_due', 'past_due'];
  const verificationDoc = 'verification.document';
  const missingVerificationDocs = verificationFields.filter(
    (field) =>
      requirements[field] && requirements[field].indexOf(verificationDoc) > -1,
  );

  return missingVerificationDocs.length > 0;
};

const AdminCarrierDetails = ({
  carrier,
  onOpenJobs,
  onOpenPassword,
  onOpenAccountRequest,
  t,
  onSaveDetails,
}) => {
  const [showModal, setShowModal] = useState(false);

  const renderModalComponent = (category) => {
    return (
      <CarrierForm
        category={category}
        details={carrier}
        onSave={(data) => onSaveDetails('Carrier', data)}
      />
    );
  };

  const renderSidebar = () => (
    <Sidebar userId={carrier._id} activePage="profile" />
  );
  const breadcrumbs = {
    pages: [
      {
        name: carrier?.name || t('CARRIER'),
        href: `/users/${carrier._id}`,
        current: true,
      },
    ],
  };

  let authorized = 'unauthorized';
  let is_broker = !!carrier.is_broker;
  if (carrier.authorized) authorized = 'authorized';
  let isVerified = 'unverified';
  let bankTransfer = 'disabled';
  let fieldsNeeded = 'no verification fields are needed';
  let requirements = {};
  let documentAlert = '';
  if (
    (isVerified === 'unverified' &&
      fieldsNeeded &&
      hasLegalEntityDocumentDue(fieldsNeeded)) ||
    (requirements && hasDocumentDue(requirements))
  ) {
    documentAlert = 'Needs DL or Passport Document';
  }

  return (
    <LayoutWithSidebar
      sidebar={renderSidebar}
      breadcrumbs={breadcrumbs}
      className="py-4"
    >
      <div className="lg:grid lg:grid-cols-2 lg:gap-4">
        <CarrierAccountDetails
          user={carrier}
          verification={{
            authorized,
            isVerified,
            bankTransfer,
            documentAlert,
          }}
          onOpenJobs={onOpenJobs}
          onOpenPassword={onOpenPassword}
          onOpenAccountRequest={onOpenAccountRequest}
          onOpenModal={(category) => setShowModal(category)}
          handleStatusUpdates={handleStatusUpdates}
          hasLoginAsPermission={false}
        />
        <div>
          <AccountContacts
            className="mb-6"
            contacts={carrier.contacts}
            userId={carrier._id}
          />
          <CargomaticCard
            header={() => <h3 className="my-0">{t('PARTNER_ACCESS')}</h3>}
            grayFooter
          >
            <CarrierAccountAccess carrier={carrier} perPage={5} />
          </CargomaticCard>
        </div>
      </div>
      {showModal && (
        <CargomaticModal
          show
          onHide={() => setShowModal(false)}
          title={t('EDIT_ACCOUNT')}
          body={renderModalComponent(showModal)}
          footer={null}
          location="middle"
        />
      )}
    </LayoutWithSidebar>
  );
};

export default AdminCarrierDetails;
